import type { CarouselSlide } from "@/features/opportunities/types/opportunities.types"
import { COMPETITOR_PATH_TOKEN, brandIconUrl } from "@nexus/contracts"
import { competitorCreativeFolder, DEFAULT_COMPETITORS_PATH, GCS_PUB_DATA_BASE } from "@/lib/creative-asset-urls"

export const GCS_BASE = GCS_PUB_DATA_BASE

/** Grouped query sentinel. It is not a storage folder under pub_data. */
export const DRILLDOWN_MULTI_COMPETITOR = "Multiple"

/** Brand used for the card name, logo, and creative URLs. The mixed-group sentinel has no folder. */
export function resolveDrilldownCompetitor(competitor: string): string {
  const name = competitor.trim()
  if (name === DRILLDOWN_MULTI_COMPETITOR) return ""
  return name
}

export function drilldownCompetitorFolder(competitor: string, competitorsPath?: string): string {
  const name = resolveDrilldownCompetitor(competitor)
  if (!name) return ""
  return competitorCreativeFolder(competitorsPath ?? DEFAULT_COMPETITORS_PATH, name)
}

/** Shared by both card mappers — the ad-library link is kept on the redesigned
 *  card (moved to the top-right of the media block) for both Competitors and
 *  My Brand, even though the Figma mock omits it. */
export const AD_LIB_BASE = "https://www.facebook.com/ads/library/?id="

export const NO_AD_LIB_URL = "#"

/** BQ `source` values that are Meta. `meta` is the competitor rollup; Facebook and Instagram are placement names. */
const META_AD_LIBRARY_SOURCES = new Set(["meta", "facebook", "instagram"])

/** Strips BQ id namespaces (`lib_id:` / `ad_id:`) down to the bare id. */
export function stripAdIdPrefix(adId: string): string {
  return adId.replace(/^(?:lib_id|ad_id):/i, "")
}

/** Bare ad id, or null when it is missing or the aggregate sentinel `Multiple`. */
export function linkableAdId(adId: string): string | null {
  const id = stripAdIdPrefix(adId).trim()
  if (!id || id.toLowerCase() === "multiple") return null
  return id
}

function isMetaAdLibrarySource(source: string): boolean {
  return META_AD_LIBRARY_SOURCES.has(source.trim().toLowerCase())
}

/** True when the card or menu should offer the Facebook Ad Library link. */
export function hasAdLibraryUrl(adLibUrl: string): boolean {
  return adLibUrl !== "" && adLibUrl !== NO_AD_LIB_URL
}

/** Facebook Ad Library URL for a Meta row, or `NO_AD_LIB_URL` when the id or source is not Meta. */
export function buildAdLibUrl(adId: string, source: string): string {
  if (!isMetaAdLibrarySource(source)) return NO_AD_LIB_URL
  const id = linkableAdId(adId)
  if (!id) return NO_AD_LIB_URL
  return `${AD_LIB_BASE}${id}`
}

const MONTHS = ["Jan", "Feb", "Mar", "Apr", "May", "Jun", "Jul", "Aug", "Sep", "Oct", "Nov", "Dec"]

export function extractString(val: unknown): string {
  if (val === null || val === undefined) return ""
  if (typeof val === "object" && "value" in (val as object)) {
    return String((val as { value: unknown }).value ?? "")
  }
  return String(val)
}

/**
 * Stable per-row key shared by the list, grid, and drawer selection. Each result row is one
 * group, so the group columns identify it; under No grouping Groupby1 is the per-asset key,
 * which keeps two assets that share a creative hash apart. Never returns "".
 */
export function buildDrilldownRowKey(row: Record<string, unknown>, index: number): string {
  const groupKey = [row.side, row.Groupby1, row.Groupby2, row.Groupby3]
    .map(extractString)
    .filter(Boolean)
    .join("|")
  return groupKey || extractString(row.hash) || extractString(row.ad_id) || `row-${index}`
}

/** Coerce a BQ numeric cell to number|null; null on missing/NaN (e.g. a SAFE_DIVIDE zero-denominator result). */
export function extractNumber(val: unknown): number | null {
  if (val === null || val === undefined) return null
  const n = Number(val)
  return Number.isNaN(n) ? null : n
}

export function formatYmd(str: string): string {
  if (!str) return "—"
  const parts = str.split("-")
  const year = parseInt(parts[0] ?? "0", 10)
  const month = parseInt(parts[1] ?? "0", 10) - 1
  const day = parseInt(parts[2] ?? "0", 10)
  if (month < 0 || month > 11 || !day || !year) return str
  return `${MONTHS[month]} ${day}, ${year}`
}

/** Card footer range: `01.01.26`. Leaves table/chart `formatYmd` (`Jan 1, 2026`) unchanged. */
export function formatYmdDotted(str: string): string {
  if (!str) return "—"
  const parts = str.split("-")
  const year = parseInt(parts[0] ?? "0", 10)
  const month = parseInt(parts[1] ?? "0", 10)
  const day = parseInt(parts[2] ?? "0", 10)
  if (month < 1 || month > 12 || !day || !year) return str
  const dd = String(day).padStart(2, "0")
  const mm = String(month).padStart(2, "0")
  const yy = String(year).slice(-2)
  return `${dd}.${mm}.${yy}`
}

/** Parses a 'YYYY-MM-DD' string into UTC year/month/day parts, or null if malformed. */
function parseYmdUtc(str: string): { y: number; m: number; d: number } | null {
  const parts = str.split("-")
  const y = parseInt(parts[0] ?? "", 10)
  const m = parseInt(parts[1] ?? "", 10) - 1
  const d = parseInt(parts[2] ?? "", 10)
  if (!str || Number.isNaN(y) || m < 0 || m > 11 || Number.isNaN(d) || !d) return null
  return { y, m, d }
}

/**
 * Inclusive calendar-day span between two 'YYYY-MM-DD' strings, matching the "N days
 * active" semantic (a creative seen on only one day reads as "1 day active", not "0").
 * This is a CLIENT-SIDE derivation and is NOT guaranteed to match Competitors'
 * backend-computed `Days` field (different source data, different computation) — the
 * two pills can look identical but carry different meanings; that's a known, accepted
 * tradeoff (My Brand has no equivalent backend field), not a bug.
 *
 * - Same day (min === max): returns 1 (inclusive).
 * - Either string empty/unparseable: returns null — the caller MUST hide the pill
 *   entirely, never render "0 days active" (that would misrepresent missing data as
 *   a real zero).
 * - Reversed order (min date > max date): defensive-clamped via Math.abs — BigQuery's
 *   MIN()/MAX() aggregates should never produce this, but a negative day count must
 *   never render.
 */
export function daysBetweenYmd(minDate: string, maxDate: string): number | null {
  const min = parseYmdUtc(minDate)
  const max = parseYmdUtc(maxDate)
  if (!min || !max) return null
  const minMs = Date.UTC(min.y, min.m, min.d)
  const maxMs = Date.UTC(max.y, max.m, max.d)
  return Math.abs(maxMs - minMs) / 86_400_000 + 1
}

export function parseCarouselSlides(
  row: Record<string, unknown>,
  competitor: string,
  competitorsPath?: string,
): CarouselSlide[] | null {
  if (extractString(row.object_type).toLowerCase() !== "carousel") return null
  const mainHash = extractString(row.hash)
  if (!mainHash) return null
  const folder = drilldownCompetitorFolder(competitor, competitorsPath)
  const fileUrl = (hash: string, kind: "thumbnails" | "assets", ext: string) =>
    folder ? `${folder}/${kind}/${hash}.${ext}` : ""

  // Slide 0: main hash — type is unknown, so first try .png (image); lightbox falls back to .mp4 on error
  const slides: CarouselSlide[] = [{
    hash: mainHash,
    type: "image",
    thumbnailUrl: fileUrl(mainHash, "thumbnails", "png"),
    assetUrl: fileUrl(mainHash, "assets", "png"),
  }]

  // Slides 1+: additional hashes with reliably known types from included_hashes_types
  const additionalHashes = extractString(row.included_hashes).split("|").filter(Boolean)
  const types = extractString(row.included_hashes_types).split("|")
  for (let i = 0; i < additionalHashes.length; i++) {
    const hash = additionalHashes[i]!
    const isVid = (types[i] ?? "image").toLowerCase() !== "image"
    slides.push({
      hash,
      type: isVid ? "video" : "image",
      thumbnailUrl: fileUrl(hash, "thumbnails", "png"),
      assetUrl: fileUrl(hash, "assets", isVid ? "mp4" : "png"),
    })
  }

  return slides
}

export interface DrilldownCompetitorMedia {
  competitor: string
  hash: string
  objectType: string
  carouselSlides: CarouselSlide[] | null
  isCarousel: boolean
  isVideo: boolean
  thumbnailUrl: string
  assetUrl: string
  logoUrl: string | null
}

/** Shared media facts for competitor card, list, and chart. Each view maps its own model. */
export function deriveDrilldownCompetitorMedia(
  row: Record<string, unknown>,
  competitorsPath?: string,
): DrilldownCompetitorMedia {
  const hash = extractString(row.hash)
  const competitor = resolveDrilldownCompetitor(extractString(row.competitor))
  const objectType = extractString(row.object_type)
  const carouselSlides = parseCarouselSlides(row, competitor, competitorsPath)
  const isCarousel = Boolean(carouselSlides)
  const isVideo = !isCarousel && objectType.toLowerCase() !== "image"
  const ext = isVideo ? "mp4" : "png"
  const folder = drilldownCompetitorFolder(competitor, competitorsPath)
  const thumbnailUrl = hash && folder ? `${folder}/thumbnails/${hash}.png` : ""
  const assetUrl = hash && folder ? `${folder}/assets/${hash}.${ext}` : ""
  const logoUrl = brandIconUrl(competitorsPath ?? DEFAULT_COMPETITORS_PATH, COMPETITOR_PATH_TOKEN, competitor)
  return { competitor, hash, objectType, carouselSlides, isCarousel, isVideo, thumbnailUrl, assetUrl, logoUrl }
}
